import * as THREE from 'three';
import type {ExteriorFace} from 'components/customer/RoomPlanner/helpers/componentGeometry';
import type {Cache} from 'shared/Cache';
import {CabinetMesh} from 'components/customer/RoomPlanner/types';
import type {getDirection} from 'components/customer/RoomPlanner/helpers/calculations';

/**
 * What every snapping algorithm is handed besides the two products: the drag
 * and the scene around it. Built once per findSnaps call.
 */
export interface SnapContext {
    // The dragged product's facts.
    selected: ProductFacts;
    // Every product's facts for this calculation, each described once.
    products: ProductCache;
    targetPos: THREE.Vector3;
    // Where the dragged product is now, before this move.
    currentPos: THREE.Vector3;
    productsRef: THREE.Group[];
    overlappingCabinets: CabinetMesh[] | null;
    camera: THREE.Camera;
    wallGroup?: THREE.Object3D | null;
}

/**
 * What the face and corner snaps and face alignment read of something a
 * product can meet: another product (ProductFacts) or the room's walls
 * (WallFacts, roomWalls.ts).
 */
export interface SurfaceFacts {
    group: THREE.Group;
    // World box. Shared, so read only: clone it before changing it.
    bbox: THREE.Box3;
    // The solid boxes a product may not land inside, whose corners are its
    // corners. Shared, so read only.
    solidBBoxes: readonly THREE.Box3[];
    // The upright faces a product can meet.
    exteriorFaces: readonly ExteriorFace[];
}

/**
 * What the router and the strategies need to know about one product, worked
 * out once per drag for the dragged product and once per pair for the other.
 */
export interface ProductFacts extends SurfaceFacts {
    group: THREE.Group;
    // World box at the product's current position. Shared, so read only:
    // clone it before changing it.
    bbox: THREE.Box3;
    // The box's width (x), height (y) and depth (z).
    size: THREE.Vector3;
    // The world box of one of the product's components, measured on first
    // ask. Shared, so read only.
    componentBBox(component: THREE.Object3D): THREE.Box3;
    // The boxes of its adjustable legs, measured on first read. Legs load
    // asynchronously, so a cabinet reads as legless until they arrive.
    legBBoxes: readonly THREE.Box3[];
    // The boxes of its box-shaped front, carcase and unknown components, or
    // its own box when it has none (componentRoles.ts). Measured on first
    // read; shared, so read only.
    solidBBoxes: readonly THREE.Box3[];
    // The upright faces of those boxes that can be seen from outside it.
    exteriorFaces: readonly ExteriorFace[];
    // What a drag collides with: every shown component. Read only.
    bodyBBoxes: readonly THREE.Box3[];
    direction: ReturnType<typeof getDirection>;
    isUpper: boolean;
    isBulkhead: boolean;
    // A kicker frame.
    isFloorProduct: boolean;
    isKickLength: boolean;
    // The flag the legacy snapping used for kick-length panels, which the
    // under-cabinet rule still reads to tell a kicker frame from a panel; it
    // differs from isKickLength on products that set only one of the two.
    isNonManualPanel: boolean;
    // A manual panel, panel, vermin strip or filler.
    isFiller: boolean;
    sitsOnFloor: boolean;
    hasLegs: boolean;
    isCornerLShape: boolean;
    // An L, key or U shaped panel.
    isNonRectShape: boolean;
    isReturn: boolean;
    isRightReturn: boolean;
    ignoresSnapping: boolean;
    // An appliance GLB. Pairs that include one use placeholder snapping.
    isPlaceholder: boolean;
}

/**
 * Products' facts by product, each described once (ProductRegistry,
 * snapping/context.ts). Whoever runs a calculation makes one and drops it
 * when the calculation ends, since nothing updates facts when a product
 * moves.
 */
export type ProductCache = Cache<THREE.Group, ProductFacts>;

/**
 * The two products one routing decision is about.
 */
export interface PairFacts {
    selected: ProductFacts;
    other: ProductFacts;
    // The tops are within 5 units of each other.
    hasMatchingTopY: boolean;
    sameDirection: boolean;
}

/**
 * A proposed position and the distance it was chosen by, so a later
 * comparison is always against the distance of the position actually held.
 */
export interface SnapCandidate {
    position: THREE.Vector3;
    distance: number;
}

/**
 * What a strategy says about one pair: a candidate, or nothing in range
 * (null).
 */
export type SnapOutcome = SnapCandidate | null;

export const isSnapCandidate = (
    outcome: SnapOutcome
): outcome is SnapCandidate => outcome !== null;

/**
 * Every snapping algorithm: the context and the pair the router matched it
 * to in, an outcome out. Changes nothing outside itself.
 */
export type SnapStrategy = (ctx: SnapContext, pair: PairFacts) => SnapOutcome;
